import { useEffect, useState } from "react";
import { Button } from "../Inquiary/components/common/Button";
import { FormField, TextInput } from "../Inquiary/components/common/FormInput";
import { RightDrawer, ModalHeader, ModalBody, ModalFooter } from "./RightDrawer";
import MasterSelect from "./MasterSelect";
import CustomSelect from "./Map/CustomerDropdown";
import { pipelineForInquiryType } from "../../utils/inquiryWorkstream";
import {
  FALLBACK_PAYMENT_STATUS,
  FALLBACK_PAYMENT_STAGE,
} from "../../utils/masterFallbacks";
import { formatDate } from "../../utils/commonutils";

const titles = {
  payment_recovery: "Start recovery file",
  gst: "Start GST case",
  customer: "Convert to customer",
};

const kickers = {
  payment_recovery: "Payment recovery",
  gst: "GST litigation",
  customer: "Customer",
};

export default function ConvertInquiryDrawer({
  open,
  onClose,
  inquiry,
  employeeData = [],
  props,
  onSubmit,
  busy,
}) {
  const pipeline = pipelineForInquiryType(inquiry?.type);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setValues({
      companyName: inquiry?.customer?.companyName || "",
      email: "",
      GST: "",
      officeAddr: "",
      buyerName: "",
      diaryNo: "",
      amount: "",
      caseNo: "",
      status: pipeline === "payment_recovery" ? "New" : "New",
      stage: pipeline === "payment_recovery" ? "Legal Notice" : "Notice",
      caseId: "",
      subject: inquiry?.service || inquiry?.notes || "",
      assignedTo: inquiry?.assignedTo?._id || props?.authData?.idToken || "",
      DRC01AReceived: formatDate(new Date()),
    });
  }, [open, inquiry, pipeline, props?.authData?.idToken]);

  if (!open) return null;

  const set = (key) => (e) => {
    const value = e.target.value;
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!values.companyName) next.companyName = "Company name is required";
    if (pipeline === "payment_recovery") {
      if (!values.buyerName) next.buyerName = "Buyer name is required";
      if (!values.diaryNo) next.diaryNo = "Diary no. is required";
      if (!values.amount) next.amount = "Amount is required";
    }
    if (pipeline === "gst") {
      if (!values.GST) next.GST = "GSTIN is required";
      if (!values.caseId) next.caseId = "Case no. is required";
      if (!values.subject) next.subject = "Subject is required";
      if (!values.amount) next.amount = "Amount is required";
      if (!values.assignedTo) next.assignedTo = "Assigned user is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const customer = { companyName: values.companyName };
    if (values.email) customer.email = values.email;
    if (values.GST) customer.GST = values.GST;
    if (values.officeAddr) customer.officeAddr = values.officeAddr;
    const body = {
      pipeline,
      customer,
    };
    if (pipeline === "payment_recovery") {
      body.paymentRecovery = {
        applicantName: inquiry.name,
        applicantMobile: inquiry.mobile,
        buyerName: values.buyerName,
        diaryNo: values.diaryNo,
        amount: values.amount,
        caseNo: values.caseNo,
        status: values.status,
        stage: values.stage,
      };
    }
    if (pipeline === "gst") {
      body.gstCase = {
        caseId: values.caseId,
        GSTIN: values.GST,
        subject: values.subject,
        amount: values.amount,
        stage: values.stage,
        status: values.status,
        assignedTo: values.assignedTo,
        DRC01AReceived: values.DRC01AReceived,
      };
    }
    onSubmit(body);
  };

  return (
    <RightDrawer isOpen={open} onClose={onClose} size="lg">
      <ModalHeader toggle={onClose} kicker={kickers[pipeline]}>
        {titles[pipeline]}
      </ModalHeader>
      <ModalBody>
        <form id="convert-inquiry-form" onSubmit={handleSubmit}>
          <div className="drawer-section">
            <h3 className="drawer-section-title">From inquiry</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="drawer-kv">
                <span className="drawer-kv-label">Name</span>
                <span className="drawer-kv-value">{inquiry?.name || "—"}</span>
              </div>
              <div className="drawer-kv">
                <span className="drawer-kv-label">Mobile</span>
                <span className="drawer-kv-value">{inquiry?.mobile || "—"}</span>
              </div>
            </div>
          </div>

          <div className="drawer-section">
            <h3 className="drawer-section-title">Customer</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Company name" required error={errors.companyName}>
                <TextInput value={values.companyName || ""} onChange={set("companyName")} placeholder="Company name" />
              </FormField>
              <FormField label="Email">
                <TextInput value={values.email || ""} onChange={set("email")} placeholder="Optional email" />
              </FormField>
              {pipeline === "gst" ? (
                <FormField label="GSTIN" required error={errors.GST}>
                  <TextInput value={values.GST || ""} onChange={set("GST")} placeholder="GSTIN" />
                </FormField>
              ) : (
                <FormField label="GSTIN">
                  <TextInput value={values.GST || ""} onChange={set("GST")} placeholder="Optional" />
                </FormField>
              )}
              <FormField label="Office address">
                <TextInput value={values.officeAddr || ""} onChange={set("officeAddr")} placeholder="Optional" />
              </FormField>
            </div>
          </div>

          {pipeline === "payment_recovery" && (
            <div className="drawer-section">
              <h3 className="drawer-section-title">Recovery file</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Buyer name" required error={errors.buyerName}>
                  <TextInput value={values.buyerName || ""} onChange={set("buyerName")} placeholder="Buyer name" />
                </FormField>
                <FormField label="Diary no." required error={errors.diaryNo}>
                  <TextInput value={values.diaryNo || ""} onChange={set("diaryNo")} placeholder="Diary no." />
                </FormField>
                <FormField label="Amount" required error={errors.amount}>
                  <TextInput type="number" value={values.amount || ""} onChange={set("amount")} placeholder="Amount" />
                </FormField>
                <FormField label="Case no.">
                  <TextInput value={values.caseNo || ""} onChange={set("caseNo")} placeholder="Optional" />
                </FormField>
                <FormField label="Status">
                  <MasterSelect
                    variant="inquiry"
                    group="payment_recovery_status"
                    fallback={FALLBACK_PAYMENT_STATUS}
                    value={values.status || ""}
                    onChange={set("status")}
                  />
                </FormField>
                <FormField label="Stage">
                  <MasterSelect
                    variant="inquiry"
                    group="payment_recovery_stage"
                    fallback={FALLBACK_PAYMENT_STAGE}
                    value={values.stage || ""}
                    onChange={set("stage")}
                  />
                </FormField>
              </div>
            </div>
          )}

          {pipeline === "gst" && (
            <div className="drawer-section">
              <h3 className="drawer-section-title">GST case</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Case no." required error={errors.caseId}>
                  <TextInput value={values.caseId || ""} onChange={set("caseId")} placeholder="Case no." />
                </FormField>
                <FormField label="Subject" required error={errors.subject}>
                  <TextInput value={values.subject || ""} onChange={set("subject")} placeholder="Subject" />
                </FormField>
                <FormField label="Amount" required error={errors.amount}>
                  <TextInput value={values.amount || ""} onChange={set("amount")} placeholder="Amount" />
                </FormField>
                <FormField label="DRC-01A received">
                  <TextInput type="date" value={values.DRC01AReceived || ""} onChange={set("DRC01AReceived")} />
                </FormField>
                <FormField label="Status">
                  <MasterSelect
                    variant="inquiry"
                    group="gst_case_status"
                    fallback={[{ value: "New", label: "New" }, { value: "Pending", label: "Pending" }]}
                    value={values.status || ""}
                    onChange={set("status")}
                  />
                </FormField>
                <FormField label="Stage">
                  <MasterSelect
                    variant="inquiry"
                    group="gst_case_stage"
                    fallback={[{ value: "Notice", label: "Notice" }, { value: "Reply", label: "Reply" }]}
                    value={values.stage || ""}
                    onChange={set("stage")}
                  />
                </FormField>
                <FormField label="Assign case to" required error={errors.assignedTo}>
                  <CustomSelect value={values.assignedTo || ""} onChange={set("assignedTo")}>
                    <option value="">Select user</option>
                    {values.assignedTo &&
                    !employeeData?.some((item) => item?.userId?._id === values.assignedTo) ? (
                      <option value={values.assignedTo}>
                        {inquiry?.assignedTo?.name || "Assigned user"}
                      </option>
                    ) : null}
                    {employeeData
                      ?.filter((item) => item?.userId?._id)
                      .map((item) => (
                        <option key={item.userId._id} value={item.userId._id}>
                          {item.name}
                        </option>
                      ))}
                  </CustomSelect>
                </FormField>
              </div>
            </div>
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button type="submit" form="convert-inquiry-form" disabled={busy}>
          {busy ? "Saving…" : titles[pipeline]}
        </Button>
      </ModalFooter>
    </RightDrawer>
  );
}
